import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Input,
  Row,
  Col,
  Select,
  DatePicker,
  Form,
  Spin,
  Typography,
  Segmented,
  message,
} from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import I18n from 'i18n-js';
import useErrors from 'hooks/forms/useErrors';
import dayjs from 'dayjs';
import { ticket_path, tickets_path } from 'js-routes/generated/routes';
import {
  arrayOf, string, func, bool,
} from 'prop-types';
import useDebounceAdminUserFetch from './hooks/useDebounceAdminUserFetch';
import useDebounceAssigneeUserFetch from './hooks/useDebounceAssigneeUserFetch';
import useSubmitForm from './hooks/useSubmitForm';
import DetailedInfo from './DetailedInfo';
import PriorityLabel from './nested/PriorityLabel';
import StatusLabel from './nested/StatusLabel';
import CwoModuleLabel from './nested/CwoModuleLabel';
import UserWithAvatarLabel from './nested/UserWithAvatarLabel';
import TicketTypeLabel from './nested/TicketTypeLabel';
import SizeLabel from './nested/SizeLabel';
import DevStatusLabel from './nested/DevStatusLabel';
import DevStageLabel from './nested/DevStageLabel';
import CommitmentLevelLabel from './nested/CommitmentLevelLabel';
import { SMALL_DEVICE_WIDTH } from './lib/constants';
import Attachments from './Attachments';
import ExternalLinks from './ExternalLinks';
import TicketContextInfo from './nested/TicketContextInfo';
import {
  responsibleUserInfoDefaultPropTypes,
  currentUserInfoDefaultPropTypes,
  responsibleUserInfoPropTypes,
  currentUserInfoPropTypes,
  selectedNodePropTypes,
  selectedNodeDefaultPropTypes,
} from './propTypes';

const { TextArea } = Input;
const { Text } = Typography;

const TicketModalForm = ({
  priorities = [],
  statuses = [],
  ticketTypes = [],
  selectedNode = selectedNodeDefaultPropTypes,
  setSelectedNode = (_node) => {},
  currentUserInfo = currentUserInfoDefaultPropTypes,
  setInitialTickets = (_tickets) => {},
  isModalOpen = false,
  setIsModalOpen = (_open) => {},
  ticketFromDashboard = false,
  responsibleUserInfo = responsibleUserInfoDefaultPropTypes,
  commitmentLevels = [],
  devStages = [],
  devStatuses = [],
  sizes = [],
  cwoModules = [],
  canSetInternalComment = false,
}) => {
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(window.innerWidth < SMALL_DEVICE_WIDTH);
  const [attachments, setAttachments] = useState(selectedNode?.ticketAttachments || []);
  const [externalLinks, setExternalLinks] = useState(selectedNode?.externalLinks || []);
  const currentTicketType = Form.useWatch('ticket_type', form);

  const resolveFormUrl = () => {
    if (selectedNode?.id) {
      return ticket_path(selectedNode.id);
    }
    return tickets_path();
  };

  const {
    handleError,
    clearErrors,
    errorStatus,
    errorMessage,
  } = useErrors();

  const {
    isFormSending,
    handleSubmitForm,
    submitButtonName,
  } = useSubmitForm({
    form,
    formUrl: resolveFormUrl(),
    clearErrors,
    handleError,
    id: selectedNode?.id,
    setIsModalOpen,
    setSelectedNode,
    setInitialTickets,
    ticketFromDashboard,
    attachments,
    setAttachments,
    externalLinks,
  });

  const {
    adminUserFetch,
    dataFetching,
    adminUsers,
  } = useDebounceAdminUserFetch({ initialOptions: [] });

  const {
    assigneeUserFetch,
    assigneeFetching,
    assigneeUsers,
  } = useDebounceAssigneeUserFetch({ initialOptions: [] });

  const userLabel = (username, avatarUrl) => (
    <UserWithAvatarLabel
      username={username}
      avatarUrl={avatarUrl}
    />
  );

  useEffect(() => {
    if (selectedNode?.id) {
      setIsModalOpen(true);
      /* eslint-disable camelcase */
      form.setFieldsValue({
        title: selectedNode.title,
        description: selectedNode.description,
        priority: selectedNode.priority,
        status: selectedNode.status,
        ticket_type: selectedNode.ticketType,
        author_id: selectedNode.author
          ? {
            value: selectedNode.author.id,
            label: userLabel(selectedNode.author.username, selectedNode.author.avatarUrl),
          }
          : null,
        author_type: selectedNode.author
          ? selectedNode.author.type
          : '',
        assignee_id: selectedNode.assignee
          ? {
            value: selectedNode.assignee.id,
            label: userLabel(selectedNode.assignee.username, selectedNode.assignee.avatarUrl),
          }
          : null,
        assignee_type: selectedNode.assignee
          ? selectedNode.assignee.type
          : '',
        responsible_user_id: selectedNode.responsibleUser
          ? {
            value: selectedNode.responsibleUser.id,
            label: userLabel(
              selectedNode.responsibleUser.username,
              selectedNode.responsibleUser.avatarUrl,
            ),
          }
          : null,
        due_date: selectedNode.dueDate
          ? dayjs(selectedNode.dueDate)
          : null,
        cwo_module: selectedNode.cwoModule || '',
        company_id: selectedNode.companyId,
        commitment_level: selectedNode.commitmentLevel || null,
        dev_stage: selectedNode.devStage || null,
        dev_status: selectedNode.devStatus || null,
        size: selectedNode.size || null,
        cwo_context: selectedNode.cwoContext || { referrer_url: globalThis.location.href },
      });
      /* eslint-enable camelcase */
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAttachments(selectedNode?.ticketAttachments || []);
    setExternalLinks(selectedNode?.externalLinks || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, selectedNode]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < SMALL_DEVICE_WIDTH);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleModalClose = () => {
    setIsModalOpen(false);

    if (!selectedNode?.id) return;

    setSelectedNode(null);
    setAttachments([]);
    setExternalLinks([]);
    form.resetFields();
  };

  const handleCopyTicketLink = () => {
    const ticketUrl = new URL(ticket_path(selectedNode.id), globalThis.location.origin).href;
    navigator.clipboard.writeText(ticketUrl);
    message.success(I18n.t('tickets.form.ticket_link_copied'));
  };

  const resolveModalTitle = () => {
    if (selectedNode?.id) {
      return (
        <span>
          {I18n.t('tickets.form.edit_ticket', { id: selectedNode.id })}
          {' '}
          <Button
            type="text"
            icon={<LinkOutlined />}
            onClick={handleCopyTicketLink}
          />
        </span>
      );
    }
    return I18n.t('tickets.form.new_ticket');
  };

  const statusOptions = statuses.map((status) => ({
    value: status,
    label: <StatusLabel status={status} />,
  }));

  const priorityOptions = priorities.map((priority) => ({
    value: priority,
    label: <PriorityLabel priority={priority} />,
  }));

  const cwoModuleOptions = cwoModules.map((cwoModule) => ({
    value: cwoModule,
    label: <CwoModuleLabel cwoModule={cwoModule} />,
  }));

  const ticketTypeOptions = ticketTypes.map((ticketType) => ({
    value: ticketType,
    label: <TicketTypeLabel type={ticketType} />,
  }));

  const commitmentLevelOptions = commitmentLevels.map((level) => ({
    value: level,
    label: <CommitmentLevelLabel commitmentLevel={level} />,
  }));

  const devStageOptions = devStages.map((stage) => ({
    value: stage,
    label: <DevStageLabel devStage={stage} />,
  }));

  const devStatusOptions = devStatuses.map((status) => ({
    value: status,
    label: <DevStatusLabel status={status} />,
  }));

  const sizeOptions = sizes.map((size) => ({
    value: size,
    label: <SizeLabel size={size} />,
  }));

  const isCurrentUserAuthor = selectedNode?.id
    ? (selectedNode?.author.id === currentUserInfo?.id
      && selectedNode?.author.type === currentUserInfo?.type)
    : true;

  return (
    <Modal
      title={resolveModalTitle()}
      open={isModalOpen}
      onCancel={handleModalClose}
      footer={null}
      width={900}
      cancelText={I18n.t('helpers.submit.ticket.cancel')}
    >
      <Form
          /* eslint-disable camelcase */
        form={form}
        layout="vertical"
        onFinish={handleSubmitForm}
        initialValues={{
          priority: 'medium',
          status: 'open',
          author_type: currentUserInfo.type,
          assignee_type: responsibleUserInfo.type,
          author_id: {
            value: currentUserInfo.id,
            label: userLabel(currentUserInfo.username, currentUserInfo.avatarUrl),
          },
          responsible_user_id: {
            value: responsibleUserInfo.id,
            label: userLabel(responsibleUserInfo.username, responsibleUserInfo.avatarUrl),
          },
          due_date: dayjs().add(1, 'week'),
          cwo_module: 'other',
          assignee_id: {
            value: responsibleUserInfo.id,
            label: userLabel(responsibleUserInfo.username, responsibleUserInfo.avatarUrl),
          },
          company_id: currentUserInfo.companyId,
          ticket_type: 'bug',
          size: 'mid',
          cwo_context: {
            referrer_url: globalThis.location.href,
          },
          /* eslint-enable camelcase */
        }}
      >
        <Row gutter={24}>
          <Col xs={24} md={16} lg={16}>
            <div className="tickets-index-page__tickets-form__left-column">
              <Form.Item name="company_id" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item name="cwo_context" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.ticket_type')}
                name="ticket_type"
                style={{ marginBottom: 12 }}
              >
                {isMobile ? (
                  <Select
                    options={ticketTypeOptions}
                    disabled={!isCurrentUserAuthor}
                  />
                ) : (
                  <Segmented
                    disabled={!isCurrentUserAuthor}
                    options={ticketTypeOptions}
                    block
                  />
                )}
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.title')}
                name="title"
                rules={[{ required: true, message: I18n.t('tickets.form.title_required') }]}
                validateStatus={errorStatus('title')}
                help={errorMessage('title')}
              >
                <TextArea
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  disabled={!isCurrentUserAuthor}
                  placeholder={I18n.t('tickets.form.title_hint')}
                />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.description')}
                name="description"
                rules={[{ required: true, message: I18n.t('tickets.form.description_required') }]}
                validateStatus={errorStatus('description')}
                help={errorMessage('description')}
                className="tickets-index-page__form-item"
              >
                <TextArea
                  autoSize={{ minRows: 6, maxRows: 20 }}
                  placeholder={I18n.t('tickets.form.description_hint')}
                  disabled={!isCurrentUserAuthor}
                />
              </Form.Item>

              <Form.Item
                label={I18n.t('tickets.form.external_links')}
                className="tickets-index-page__form-item"
              >
                <ExternalLinks
                  links={externalLinks}
                  onChange={setExternalLinks}
                />
              </Form.Item>

              {(isCurrentUserAuthor || attachments.length > 0) && (
                <Form.Item
                  label={I18n.t('tickets.form.attachments')}
                  name="files"
                  validateStatus={errorStatus('files')}
                  help={errorMessage('files')}
                  className="tickets-index-page__form-item"
                >
                  <Attachments
                    fileList={attachments}
                    setFileList={setAttachments}
                    isCurrentUserAuthor={isCurrentUserAuthor}
                  />
                </Form.Item>
              )}

              {selectedNode?.id && (
              <>
                <hr className="tickets-index-page__horizontal-line" />
                <DetailedInfo
                  ticketId={selectedNode?.id}
                  currentUserInfo={currentUserInfo}
                  canSetInternalComment={canSetInternalComment}
                />
              </>
              )}

            </div>
          </Col>

          <Col
            xs={24}
            md={8}
            lg={8}
            className="tickets-index-page__tickets-form__right-column-with-border"
          >
            <div className="tickets-index-page__tickets-form__right-column">
              {selectedNode?.cwoContext?.referrer_url && (
                <TicketContextInfo url={selectedNode.cwoContext.referrer_url} />
              )}

              <Form.Item name="author_type" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.author')}
                name="author_id"
                validateStatus={errorStatus('author')}
                help={errorMessage('author')}
                className="tickets-index-page__form-item"
              >
                <Select
                  labelInValue
                  placeholder={I18n.t('search_with_min_input', { count: 3 })}
                  showSearch
                  filterOption={false}
                  autoClearSearchValue={false}
                  onSearch={adminUserFetch}
                  notFoundContent={dataFetching ? <Spin size="small" /> : null}
                  options={adminUsers}
                  loading={dataFetching}
                  fieldNames={{ label: 'username', value: 'user_id' }}
                  disabled
                />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.responsible_user')}
                name="responsible_user_id"
                validateStatus={errorStatus('responsible_user_id')}
                help={errorMessage('responsible_user_id')}
                className="tickets-index-page__form-item"
              >
                <Select
                  labelInValue
                  placeholder={I18n.t('search_with_min_input', { count: 3 })}
                  showSearch
                  filterOption={false}
                  autoClearSearchValue={false}
                  onSearch={adminUserFetch}
                  notFoundContent={dataFetching ? <Spin size="small" /> : null}
                  options={adminUsers}
                  loading={dataFetching}
                  fieldNames={{ label: 'username', value: 'admin_user_id' }}
                  disabled
                />
              </Form.Item>

              <Form.Item name="assignee_type" hidden>
                <Input type="hidden" />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.assignee')}
                name="assignee_id"
                validateStatus={errorStatus('assignee_id')}
                help={errorMessage('assignee_id')}
                className="tickets-index-page__form-item"
              >
                <Select
                  labelInValue
                  optionLabelProp="label"
                  placeholder={I18n.t('search_with_min_input', { count: 3 })}
                  showSearch
                  filterOption={false}
                  autoClearSearchValue={false}
                  onSearch={assigneeUserFetch}
                  notFoundContent={assigneeFetching ? <Spin size="small" /> : null}
                  loading={assigneeFetching}
                  onChange={(_value, option) => {
                    const selectedOption = Array.isArray(option) ? option[0] : option;

                    form.setFieldsValue({
                      // eslint-disable-next-line camelcase
                      assignee_type: selectedOption?.user_type,
                    });
                  }}
                >
                  {assigneeUsers.map((user) => (
                    <Select.Option
                      key={user.key}
                      value={user.id}
                      user_type={user.user_type}
                      label={(
                        <UserWithAvatarLabel
                          username={user.username}
                          avatarUrl={user.avatarUrl}
                        />
                      )}
                    >
                      <UserWithAvatarLabel username={user.username} avatarUrl={user.avatarUrl} />
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.priority')}
                name="priority"
                validateStatus={errorStatus('priority')}
                help={errorMessage('priority')}
                className="tickets-index-page__form-item"
              >
                <Select
                  options={priorityOptions}
                  disabled={!isCurrentUserAuthor}
                />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.status')}
                name="status"
                validateStatus={errorStatus('status')}
                help={errorMessage('status')}
                className="tickets-index-page__form-item"
              >
                <Select
                  options={statusOptions}
                />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.cwo_module')}
                name="cwo_module"
                validateStatus={errorStatus('cwo_module')}
                help={errorMessage('cwo_module')}
                className="tickets-index-page__form-item"
              >
                <Select
                  options={cwoModuleOptions}
                />
              </Form.Item>

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.size')}
                name="size"
                validateStatus={errorStatus('size')}
                help={errorMessage('size')}
                className="tickets-index-page__form-item"
              >
                <Select
                  options={sizeOptions}
                />
              </Form.Item>

              {
                currentTicketType === 'feature_request' && (
                  <>
                    <Form.Item
                      label={I18n.t('activerecord.attributes.ticket.commitment_level')}
                      name="commitment_level"
                      validateStatus={errorStatus('commitment_level')}
                      help={errorMessage('commitment_level')}
                      className="tickets-index-page__form-item"
                    >
                      <Select
                        options={commitmentLevelOptions}
                        allowClear
                      />
                    </Form.Item>

                    <Form.Item
                      label={I18n.t('activerecord.attributes.ticket.dev_stage')}
                      name="dev_stage"
                      validateStatus={errorStatus('dev_stage')}
                      help={errorMessage('dev_stage')}
                      className="tickets-index-page__form-item"
                    >
                      <Select
                        options={devStageOptions}
                        allowClear
                      />
                    </Form.Item>

                    <Form.Item
                      label={I18n.t('activerecord.attributes.ticket.dev_status')}
                      name="dev_status"
                      validateStatus={errorStatus('dev_status')}
                      help={errorMessage('dev_status')}
                      className="tickets-index-page__form-item"
                    >
                      <Select
                        options={devStatusOptions}
                        allowClear
                      />
                    </Form.Item>
                  </>
                )
              }

              <Form.Item
                label={I18n.t('activerecord.attributes.ticket.due_date')}
                name="due_date"
                validateStatus={errorStatus('due_date')}
                help={errorMessage('due_date')}
                className="tickets-index-page__form-item"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="LL"
                  disabled={!isCurrentUserAuthor}
                  allowClear={false}
                />
              </Form.Item>

              {selectedNode?.id && (
              <Text
                type="secondary"
                className="tickets-index-page__form-created-at"
              >
                {dayjs(selectedNode.createdAt).format('LL HH:mm')}
              </Text>
              )}

              <div className="tickets-index-page__ticket-form-submit-btn">
                <Button
                  type="primary"
                  loading={isFormSending}
                  onClick={() => form.submit()}
                >
                  {submitButtonName}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

TicketModalForm.propTypes = {
  priorities: arrayOf(string),
  statuses: arrayOf(string),
  ticketTypes: arrayOf(string),
  selectedNode: selectedNodePropTypes,
  setSelectedNode: func,
  currentUserInfo: currentUserInfoPropTypes,
  setInitialTickets: func,
  isModalOpen: bool,
  setIsModalOpen: func,
  ticketFromDashboard: bool,
  responsibleUserInfo: responsibleUserInfoPropTypes,
  commitmentLevels: arrayOf(string),
  devStages: arrayOf(string),
  devStatuses: arrayOf(string),
  sizes: arrayOf(string),
  cwoModules: arrayOf(string),
  canSetInternalComment: bool,
};

export default TicketModalForm;
